// Allowlist of hash anchors that auth flows are permitted to redirect back to.
// Keep in sync with section ids rendered on the landing page.
export const ALLOWED_HASH_ANCHORS = new Set([
  "hero",
  "features",
  "proof",
  "testimonials",
  "pricing",
  "faq",
]);

// Allowlist of in-app path prefixes that auth can redirect to.
// Each entry matches either the exact path or `${prefix}/...`.
export const ALLOWED_PATH_PREFIXES = [
  "/",
  "/dashboard",
  "/onboarding",
  "/support",
  "/account",
] as const;

const MAX_REDIRECT_LENGTH = 256;

/**
 * Returns the input only if it is a safe same-origin redirect target.
 *
 * Accepts:
 *  - "#anchor" where `anchor` is in ALLOWED_HASH_ANCHORS
 *  - "/path" or "/path/sub?query" where the leading path segment is in
 *    ALLOWED_PATH_PREFIXES. Optional `?query` and `#hash` are preserved,
 *    but the hash must also pass the anchor allowlist.
 *
 * Rejects anything else: protocol-relative URLs (`//evil`), absolute URLs
 * (`https://evil`), backslash tricks, control chars, overlong strings, and
 * paths not on the allowlist.
 */
export function sanitizeRedirect(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (typeof raw !== "string") return null;
  if (raw.length === 0 || raw.length > MAX_REDIRECT_LENGTH) return null;

  // Reject control chars, whitespace, backslashes (CRLF injection / path tricks).
  if (/[\u0000-\u001F\u007F\s\\]/.test(raw)) return null;

  // Hash-only redirect — e.g. "#pricing"
  if (raw.startsWith("#")) {
    const anchor = raw.slice(1);
    return ALLOWED_HASH_ANCHORS.has(anchor) ? raw : null;
  }

  // Must be a same-origin relative path. Reject protocol-relative ("//...")
  // and any absolute URL.
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;

  // Parse using a dummy origin so we can validate path / hash separately
  // without leaking to navigation.
  let url: URL;
  try {
    url = new URL(raw, "http://localhost");
  } catch {
    return null;
  }

  // Guard against parser surprises (URL parsing should keep us same-origin
  // since input started with "/", but double-check).
  if (url.origin !== "http://localhost") return null;

  // Path must equal an allowlisted prefix exactly, or sit under one as
  // `${prefix}/...`. "/" only matches the exact root — it does NOT grant
  // access to every path.
  const path = url.pathname;
  const pathAllowed = ALLOWED_PATH_PREFIXES.some(
    (p) => path === p || (p !== "/" && path.startsWith(`${p}/`)),
  );
  if (!pathAllowed) return null;


  // If a hash is present, it must also be on the anchor allowlist.
  if (url.hash) {
    const anchor = url.hash.slice(1);
    if (!ALLOWED_HASH_ANCHORS.has(anchor)) return null;
  }

  return raw;
}
